import * as React from 'react'
import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Modal } from '@/components/primitives/Modal'
import { useAuth } from '@/features/auth/AuthContext'
import { updateProfile, changePassword } from '@/features/auth/api'
import { ROLE_LABELS } from '@/features/auth/types'
import { useToast } from '@/components/feedback/ToastProvider'
import { ApiError } from '@/lib/api'
import '@/pages/ProfilePage.css'

interface Props {
  open: boolean
  onClose: () => void
}

export function ProfileModal({ open, onClose }: Props): React.JSX.Element {
  const { user, logout, refresh } = useAuth()
  const navigate = useNavigate()
  const toast = useToast()

  /* ── Inline name edit ─────────────────────────────────────────── */
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(user?.name ?? '')

  const saveName = useMutation({
    mutationFn: () => updateProfile({ name: name.trim() }),
    onSuccess: async () => {
      await refresh()
      setEditing(false)
      toast.success('Profile updated', 'Your name has been saved.')
    },
    onError: (err) => {
      toast.error('Could not update', err instanceof ApiError ? err.message : 'Try again.')
    },
  })

  function cancelEdit(): void {
    setName(user?.name ?? '')
    setEditing(false)
  }

  /* ── Change password ──────────────────────────────────────────── */
  const [currentPw, setCurrentPw] = useState('')
  const [newPw, setNewPw] = useState('')
  const [confirmPw, setConfirmPw] = useState('')
  const [showCurrent, setShowCurrent] = useState(false)
  const [showNew, setShowNew] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [pwErrors, setPwErrors] = useState<{ current?: string; new?: string; confirm?: string }>({})

  const savePw = useMutation({
    mutationFn: () => changePassword({ current_password: currentPw, new_password: newPw }),
    onSuccess: () => {
      setCurrentPw(''); setNewPw(''); setConfirmPw('')
      setPwErrors({})
      toast.success('Password changed', 'Your new password is now active.')
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'password_invalid') {
        setPwErrors({ current: 'Current password is incorrect.' })
      } else {
        toast.error('Could not change password', err instanceof ApiError ? err.message : 'Try again.')
      }
    },
  })

  function handleSavePw(e: React.SyntheticEvent): void {
    e.preventDefault()
    const errs: typeof pwErrors = {}
    if (!currentPw) errs.current = 'Required.'
    if (!newPw) errs.new = 'Required.'
    else if (newPw.length < 8) errs.new = 'Min. 8 characters.'
    if (newPw && newPw !== confirmPw) errs.confirm = 'Passwords do not match.'
    if (Object.keys(errs).length) { setPwErrors(errs); return }
    setPwErrors({})
    savePw.mutate()
  }

  const canUpdatePw = currentPw && newPw && confirmPw

  async function handleSignOut(): Promise<void> {
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title="Profile"
      subtitle="Manage your account and password"
    >
      <div className="profile-content profile-content--drawer">
        {/* ── Profile info section ────────────────────────────── */}
        <div className="profile-panel">
          <div className="profile-panel-head">
            <h2 className="profile-panel-title">Account</h2>
            {!editing ? (
              <button type="button" className="profile-edit-btn" onClick={() => setEditing(true)}>
                <PencilIcon /> Edit
              </button>
            ) : (
              <div className="profile-edit-actions">
                <button type="button" className="profile-cancel-btn" onClick={cancelEdit} disabled={saveName.isPending}>
                  Cancel
                </button>
                <button
                  type="button"
                  className="profile-save-btn"
                  onClick={() => saveName.mutate()}
                  disabled={!name.trim() || saveName.isPending}
                >
                  {saveName.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            )}
          </div>

          <div className="profile-rows">
            <div className="profile-row">
              <span className="profile-row-label">Name</span>
              {editing ? (
                <input
                  className="profile-inline-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                  autoComplete="name"
                  onKeyDown={(e) => { if (e.key === 'Enter') saveName.mutate(); if (e.key === 'Escape') cancelEdit() }}
                />
              ) : (
                <span className="profile-row-value">{user?.name}</span>
              )}
            </div>

            <div className="profile-row">
              <span className="profile-row-label">Email</span>
              <span className="profile-row-value">{user?.email}</span>
            </div>

            <div className="profile-row">
              <span className="profile-row-label">Role</span>
              <span className="manage-role-badge">{user ? ROLE_LABELS[user.role] : ''}</span>
            </div>
          </div>

          <div className="profile-signout-row">
            <span className="profile-signout-hint">Sign out of this session on this device.</span>
            <button type="button" className="profile-signout-btn" onClick={() => void handleSignOut()}>
              <SignOutIcon /> Sign out
            </button>
          </div>
        </div>

        {/* ── Change password section ──────────────────────────── */}
        <div className="profile-panel">
          <div className="profile-panel-head profile-panel-head--stack">
            <h2 className="profile-panel-title">Change password</h2>
            <p className="profile-panel-sub">Choose a strong password and don&apos;t reuse it on other sites.</p>
          </div>

          <form onSubmit={handleSavePw} noValidate className="profile-pw-form">
            <div className="profile-pw-field">
              <label className="profile-pw-label" htmlFor="cur-pw">Current password</label>
              <div className={'profile-pw-wrap' + (pwErrors.current ? ' profile-pw-wrap--error' : '')}>
                <LockIcon />
                <input
                  id="cur-pw"
                  type={showCurrent ? 'text' : 'password'}
                  className="profile-pw-input"
                  placeholder="Enter your current password"
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  autoComplete="current-password"
                />
                <button type="button" className="profile-pw-eye" onClick={() => setShowCurrent((v) => !v)} tabIndex={-1}>
                  {showCurrent ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {pwErrors.current && <p className="profile-pw-error">{pwErrors.current}</p>}
            </div>

            <div className="profile-pw-field">
              <label className="profile-pw-label" htmlFor="new-pw">New password</label>
              <div className={'profile-pw-wrap' + (pwErrors.new ? ' profile-pw-wrap--error' : '')}>
                <LockIcon />
                <input
                  id="new-pw"
                  type={showNew ? 'text' : 'password'}
                  className="profile-pw-input"
                  placeholder="Min. 8 characters"
                  value={newPw}
                  onChange={(e) => setNewPw(e.target.value)}
                  autoComplete="new-password"
                />
                <button type="button" className="profile-pw-eye" onClick={() => setShowNew((v) => !v)} tabIndex={-1}>
                  {showNew ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {pwErrors.new && <p className="profile-pw-error">{pwErrors.new}</p>}
            </div>

            <div className="profile-pw-field">
              <label className="profile-pw-label" htmlFor="confirm-pw">Confirm new password</label>
              <div className={'profile-pw-wrap' + (pwErrors.confirm ? ' profile-pw-wrap--error' : '')}>
                <LockIcon />
                <input
                  id="confirm-pw"
                  type={showConfirm ? 'text' : 'password'}
                  className="profile-pw-input"
                  placeholder="Repeat new password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  autoComplete="new-password"
                />
                <button type="button" className="profile-pw-eye" onClick={() => setShowConfirm((v) => !v)} tabIndex={-1}>
                  {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                </button>
              </div>
              {pwErrors.confirm && <p className="profile-pw-error">{pwErrors.confirm}</p>}
            </div>

            <button
              type="submit"
              className={'profile-update-btn' + (!canUpdatePw ? ' profile-update-btn--dim' : '')}
              disabled={savePw.isPending}
            >
              {savePw.isPending ? 'Updating…' : 'Update password'}
            </button>
          </form>
        </div>
      </div>
    </Modal>
  )
}

/* ── Icons ──────────────────────────────────────────────────────── */

function PencilIcon(): React.JSX.Element {
  return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
}
function SignOutIcon(): React.JSX.Element {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
}
function LockIcon(): React.JSX.Element {
  return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="profile-pw-lock"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
}
function EyeIcon(): React.JSX.Element {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
}
function EyeOffIcon(): React.JSX.Element {
  return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
}
